/**
 * Mobile navigation drawer for the inspector layout - the dark-teal left
 * Drawer with the nav item list (simple links, dropdown groups, the
 * resources "Links" section) and the bottom user section (avatar + role
 * chip + switch-franchise / admin-panel / logout actions).
 *
 * Moved out of `InspectorLayout.tsx` unchanged (pure structural move) -
 * purely presentational; auth/visibility decisions and the shared
 * avatar-image-error state stay with the layout and arrive via props.
 */
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import {
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
  SwapHoriz as SwitchIcon,
} from '@mui/icons-material'
import {
  getCurrentActiveIndex,
  type NavigationItem,
} from '../../../config/inspectorNavigation'
import { getProfileImageUrl, getUserInitials } from '../../../utils/profileImage'
import { getRoleDisplayName, getRoleHexColor } from '../../../utils/roleDisplay'
import type { FranchisePartnerResource } from '../../../types/resources'
import type { User } from '../../../services/userApi'

interface InspectorMobileNavDrawerProps {
  open: boolean
  onClose: () => void
  fpCode?: string
  navItems: NavigationItem[]
  resources?: FranchisePartnerResource[]
  user?: User
  authLoading: boolean
  showAuthLoading: boolean
  imageError: boolean
  onImageError: () => void
  canSwitchFranchise: boolean
  hasAdminAccess: boolean
  onAdminPanel: () => void
  onLogout: () => void
}

const InspectorMobileNavDrawer: React.FC<InspectorMobileNavDrawerProps> = ({
  open,
  onClose,
  fpCode,
  navItems,
  resources,
  user,
  authLoading,
  showAuthLoading,
  imageError,
  onImageError,
  canSwitchFranchise,
  hasAdminAccess,
  onAdminPanel,
  onLogout,
}) => {
  const location = useLocation()

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      className="no-print"
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          backgroundColor: '#007f98',
          color: 'white'
        }
      }}
    >
      <Box sx={{ pt: 2, pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Navigation Items */}
        <List sx={{ flexGrow: 1 }}>
          {navItems.map((item, index) => {
            const basePath = `/inspect/${fpCode}`
            const activeIndex = getCurrentActiveIndex(location.pathname, basePath)
            const isActive = activeIndex === index

            if (item.type === 'resources_dropdown') {
              return (
                <React.Fragment key={item.path}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        mb: 0.5,
                        color: 'inherit',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                            fontWeight: 400,
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {[...(resources ?? [])]
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map(resource => {
                    const url = resource.url
                    if (resource.resource_code === 'customer_intelligence' && !url) return null
                    return (
                      <ListItem key={resource.resource_code} disablePadding>
                        <ListItemButton
                          component={url ? 'a' : 'li'}
                          href={url || undefined}
                          target={url ? '_blank' : undefined}
                          rel={url ? 'noopener noreferrer' : undefined}
                          disabled={!url}
                          onClick={onClose}
                          sx={{
                            borderRadius: 2,
                            mx: 2,
                            mb: 0.5,
                            pl: 2,
                            color: 'inherit',
                            textDecoration: 'none',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                          }}
                        >
                          <ListItemText
                            primary={resource.label}
                            secondary={!url ? 'Not configured' : undefined}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: 'white',
                                fontSize: '0.9rem',
                                opacity: url ? 1 : 0.5,
                              },
                              '& .MuiListItemText-secondary': {
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: '0.7rem',
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )
                  })}
                </React.Fragment>
              )
            } else if (item.type === 'dropdown' && item.dropdownItems) {
              return (
                <React.Fragment key={item.path}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isActive}
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        mb: 0.5,
                        color: 'inherit',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                            fontWeight: isActive ? 600 : 400,
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {/* Dropdown items for mobile */}
                  {item.dropdownItems.map((dropdownItem) => {
                    const to = `${basePath}/${dropdownItem.path}`
                    return (
                    <ListItem key={dropdownItem.path} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={to}
                        onClick={onClose}
                        sx={{
                          borderRadius: 2,
                          mx: 2,
                          mb: 0.5,
                          pl: 2,
                          color: 'inherit',
                          textDecoration: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        <ListItemText
                          primary={dropdownItem.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: 'white',
                              fontSize: '0.9rem',
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                  })}
                </React.Fragment>
              )
            } else {
              const to = item.path === '' ? basePath : `${basePath}/${item.path}`
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={to}
                    onClick={onClose}
                    selected={isActive}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      color: 'inherit',
                      textDecoration: 'none',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'white',
                          fontWeight: isActive ? 600 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            }
          })}
        </List>

        {/* User Info at Bottom */}
        <Box sx={{ px: 2, py: 1, mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          {authLoading && showAuthLoading ? (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
              <CircularProgress size={16} sx={{ color: 'white' }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 400,
                  fontSize: '0.8rem'
                }}
              >
                Loading...
              </Typography>
            </Stack>
          ) : user ? (
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar
                  src={!imageError ? getProfileImageUrl(user) : undefined}
                  onError={onImageError}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {getUserInitials(user)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      lineHeight: 1.2
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Chip
                    label={getRoleDisplayName(user.role)}
                    size="small"
                    sx={{
                      height: 14,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      bgcolor: getRoleHexColor(user.role),
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 0.4,
                      },
                    }}
                  />
                </Box>
              </Stack>
              <Stack spacing={0.5}>
                {canSwitchFranchise && (
                  <ListItemButton
                    component={Link}
                    to="/choose-franchise"
                    sx={{
                      borderRadius: 1,
                      py: 0.5,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 32 }}>
                      <SwitchIcon sx={{ fontSize: '1rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Switch Franchise"
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'white',
                          fontSize: '0.8rem',
                        }
                      }}
                    />
                  </ListItemButton>
                )}
                {hasAdminAccess && (
                  <ListItemButton
                    onClick={onAdminPanel}
                    sx={{
                      borderRadius: 1,
                      py: 0.5,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 32 }}>
                      <AdminIcon sx={{ fontSize: '1rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Admin Panel"
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'white',
                          fontSize: '0.8rem',
                        }
                      }}
                    />
                  </ListItemButton>
                )}
                <ListItemButton
                  onClick={onLogout}
                  sx={{
                    borderRadius: 1,
                    py: 0.5,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'white', minWidth: 32 }}>
                    <LogoutIcon sx={{ fontSize: '1rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: 'white',
                        fontSize: '0.8rem',
                      }
                    }}
                  />
                </ListItemButton>
              </Stack>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningIcon sx={{ color: '#ffab40', fontSize: '1.1rem' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#ffab40',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                NOT AUTHENTICATED
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default InspectorMobileNavDrawer
